/**
 * SoundGenerator - Procedural 8-bit sound effect generator
 *
 * Generates retro arcade-style sound effects using Web Audio API.
 * No external audio files needed - all sounds are synthesized.
 *
 * Quality considerations:
 * - Uses proper audio context management
 * - Generates sounds as base64 WAV data URLs
 * - Cached after generation for performance
 * - Authentic 8-bit style waveforms (square, triangle, sawtooth)
 */

/**
 * Waveform types for sound synthesis
 */
type WaveformType = 'sine' | 'square' | 'triangle' | 'sawtooth' | 'noise';

/**
 * Envelope parameters for ADSR
 */
interface Envelope {
  attack: number; // seconds
  decay: number; // seconds
  sustain: number; // level 0-1
  release: number; // seconds
}

/**
 * Sound generation parameters
 */
interface SoundParams {
  /** Duration in seconds */
  duration: number;
  /** Starting frequency in Hz */
  startFreq: number;
  /** Ending frequency in Hz (for pitch slides) */
  endFreq?: number;
  /** Waveform type */
  waveform: WaveformType;
  /** Volume envelope */
  envelope: Envelope;
  /** Master volume 0-1 */
  volume: number;
  /** Optional vibrato (frequency modulation) */
  vibrato?: { rate: number; depth: number };
  /** Optional tremolo (amplitude modulation) */
  tremolo?: { rate: number; depth: number };
}

/**
 * Sample rate for generated audio
 */
const SAMPLE_RATE = 44100;

/**
 * Generate a WAV data URL from sound parameters
 */
export function generateSound(params: SoundParams): string {
  const {
    duration,
    startFreq,
    endFreq = startFreq,
    waveform,
    envelope,
    volume,
    vibrato,
    tremolo,
  } = params;

  const numSamples = Math.floor(SAMPLE_RATE * duration);
  const samples = new Float32Array(numSamples);

  // Calculate total envelope time
  const attackSamples = Math.floor(envelope.attack * SAMPLE_RATE);
  const decaySamples = Math.floor(envelope.decay * SAMPLE_RATE);
  const releaseSamples = Math.floor(envelope.release * SAMPLE_RATE);
  const sustainSamples = numSamples - attackSamples - decaySamples - releaseSamples;

  // Phase accumulator for oscillator
  let phase = 0;

  for (let i = 0; i < numSamples; i++) {
    const t = i / SAMPLE_RATE;
    const progress = i / numSamples;

    // Calculate frequency with optional slide
    let freq = startFreq + (endFreq - startFreq) * progress;

    // Apply vibrato
    if (vibrato) {
      freq += Math.sin(2 * Math.PI * vibrato.rate * t) * vibrato.depth;
    }

    // Calculate envelope amplitude
    let envAmp: number;
    if (i < attackSamples) {
      // Attack phase
      envAmp = i / attackSamples;
    } else if (i < attackSamples + decaySamples) {
      // Decay phase
      const decayProgress = (i - attackSamples) / decaySamples;
      envAmp = 1 - (1 - envelope.sustain) * decayProgress;
    } else if (i < attackSamples + decaySamples + sustainSamples) {
      // Sustain phase
      envAmp = envelope.sustain;
    } else {
      // Release phase
      const releaseProgress = (i - attackSamples - decaySamples - sustainSamples) / releaseSamples;
      envAmp = envelope.sustain * (1 - releaseProgress);
    }

    // Apply tremolo
    if (tremolo) {
      envAmp *= 1 - tremolo.depth * 0.5 + tremolo.depth * 0.5 * Math.sin(2 * Math.PI * tremolo.rate * t);
    }

    // Generate waveform sample
    let sample: number;
    const phaseInc = freq / SAMPLE_RATE;
    phase += phaseInc;
    if (phase >= 1) phase -= 1;

    switch (waveform) {
      case 'sine':
        sample = Math.sin(2 * Math.PI * phase);
        break;
      case 'square':
        sample = phase < 0.5 ? 1 : -1;
        break;
      case 'triangle':
        sample = 4 * Math.abs(phase - 0.5) - 1;
        break;
      case 'sawtooth':
        sample = 2 * phase - 1;
        break;
      case 'noise':
        sample = Math.random() * 2 - 1;
        break;
      default:
        sample = 0;
    }

    samples[i] = sample * envAmp * volume;
  }

  // Convert to WAV
  return samplesToWavDataUrl(samples);
}

/**
 * Convert float samples to WAV data URL
 */
function samplesToWavDataUrl(samples: Float32Array): string {
  const numSamples = samples.length;
  const bytesPerSample = 2; // 16-bit
  const dataSize = numSamples * bytesPerSample;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);

  // WAV header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, totalSize - 8, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // audio format (PCM)
  view.setUint16(22, 1, true); // num channels (mono)
  view.setUint32(24, SAMPLE_RATE, true);
  view.setUint32(28, SAMPLE_RATE * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Write samples
  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    const sampleValue = samples[i] ?? 0;
    const sample = Math.max(-1, Math.min(1, sampleValue));
    const intSample = Math.floor(sample * 32767);
    view.setInt16(offset, intSample, true);
    offset += 2;
  }

  // Convert to base64 data URL
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i] ?? 0;
    binary += String.fromCharCode(byte);
  }
  return 'data:audio/wav;base64,' + btoa(binary);
}

/**
 * Helper to write string to DataView
 */
function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

/**
 * Pre-defined sound effect generators
 * These create the actual game sounds with carefully tuned parameters
 */
export const SoundEffects = {
  /**
   * Food eating sound - quick satisfying pop/blip
   */
  eat(): string {
    return generateSound({
      duration: 0.1,
      startFreq: 900,
      endFreq: 350,
      waveform: 'square',
      envelope: { attack: 0.003, decay: 0.025, sustain: 0.35, release: 0.07 },
      volume: 0.45,
    });
  },

  /**
   * Food combo eating sound - higher pitch, more rewarding
   */
  eatCombo(): string {
    return generateSound({
      duration: 0.14,
      startFreq: 1100,
      endFreq: 550,
      waveform: 'square',
      envelope: { attack: 0.003, decay: 0.04, sustain: 0.45, release: 0.1 },
      volume: 0.55,
    });
  },

  /**
   * Powerup collection sound - magical ascending sparkle
   */
  powerup(): string {
    return generateSound({
      duration: 0.35,
      startFreq: 350,
      endFreq: 1400,
      waveform: 'square',
      envelope: { attack: 0.008, decay: 0.12, sustain: 0.55, release: 0.22 },
      volume: 0.55,
      vibrato: { rate: 15, depth: 50 },
    });
  },

  /**
   * Boost start sound - powerful whoosh/acceleration
   */
  boostStart(): string {
    return generateSound({
      duration: 0.18,
      startFreq: 120,
      endFreq: 550,
      waveform: 'sawtooth',
      envelope: { attack: 0.015, decay: 0.06, sustain: 0.65, release: 0.1 },
      volume: 0.45,
    });
  },

  /**
   * Boost loop sound - energetic engine hum
   */
  boostLoop(): string {
    return generateSound({
      duration: 0.5,
      startFreq: 140,
      endFreq: 140,
      waveform: 'sawtooth',
      envelope: { attack: 0.04, decay: 0.1, sustain: 0.85, release: 0.1 },
      volume: 0.28,
      tremolo: { rate: 10, depth: 0.35 },
    });
  },

  /**
   * Boost end sound - smooth deceleration
   */
  boostEnd(): string {
    return generateSound({
      duration: 0.22,
      startFreq: 450,
      endFreq: 80,
      waveform: 'sawtooth',
      envelope: { attack: 0.008, decay: 0.06, sustain: 0.5, release: 0.16 },
      volume: 0.4,
    });
  },

  /**
   * Death sound - dramatic explosion/crash
   */
  death(): string {
    return generateSound({
      duration: 0.6,
      startFreq: 250,
      endFreq: 30,
      waveform: 'noise',
      envelope: { attack: 0.008, decay: 0.18, sustain: 0.45, release: 0.42 },
      volume: 0.7,
      tremolo: { rate: 15, depth: 0.4 },
    });
  },

  /**
   * Kill sound - triumphant victory fanfare
   */
  kill(): string {
    return generateSound({
      duration: 0.3,
      startFreq: 550,
      endFreq: 1000,
      waveform: 'square',
      envelope: { attack: 0.008, decay: 0.1, sustain: 0.65, release: 0.19 },
      volume: 0.6,
      vibrato: { rate: 12, depth: 35 },
    });
  },

  /**
   * Combo sound - exciting escalating celebration
   */
  combo(): string {
    return generateSound({
      duration: 0.4,
      startFreq: 450,
      endFreq: 1600,
      waveform: 'square',
      envelope: { attack: 0.008, decay: 0.12, sustain: 0.55, release: 0.28 },
      volume: 0.6,
      vibrato: { rate: 18, depth: 70 },
    });
  },

  /**
   * UI click sound - crisp button press
   */
  uiClick(): string {
    return generateSound({
      duration: 0.05,
      startFreq: 1000,
      endFreq: 800,
      waveform: 'square',
      envelope: { attack: 0.002, decay: 0.02, sustain: 0.2, release: 0.028 },
      volume: 0.3,
    });
  },

  /**
   * UI hover sound - subtle blip
   */
  uiHover(): string {
    return generateSound({
      duration: 0.03,
      startFreq: 1200,
      endFreq: 1000,
      waveform: 'sine',
      envelope: { attack: 0.005, decay: 0.01, sustain: 0.3, release: 0.015 },
      volume: 0.15,
    });
  },

  /**
   * UI success sound - positive confirmation
   */
  uiSuccess(): string {
    return generateSound({
      duration: 0.2,
      startFreq: 600,
      endFreq: 1000,
      waveform: 'triangle',
      envelope: { attack: 0.01, decay: 0.05, sustain: 0.5, release: 0.14 },
      volume: 0.4,
    });
  },

  /**
   * UI error sound - negative/warning feedback
   */
  uiError(): string {
    return generateSound({
      duration: 0.15,
      startFreq: 300,
      endFreq: 150,
      waveform: 'square',
      envelope: { attack: 0.01, decay: 0.04, sustain: 0.4, release: 0.1 },
      volume: 0.4,
    });
  },

  /**
   * Grow sound - satisfying size increase
   */
  grow(): string {
    return generateSound({
      duration: 0.12,
      startFreq: 300,
      endFreq: 500,
      waveform: 'triangle',
      envelope: { attack: 0.01, decay: 0.03, sustain: 0.4, release: 0.08 },
      volume: 0.35,
    });
  },

  /**
   * Spawn sound - worm entering the game
   */
  spawn(): string {
    return generateSound({
      duration: 0.4,
      startFreq: 200,
      endFreq: 800,
      waveform: 'triangle',
      envelope: { attack: 0.05, decay: 0.1, sustain: 0.5, release: 0.25 },
      volume: 0.5,
      vibrato: { rate: 8, depth: 40 },
    });
  },

  /**
   * Level up sound - achievement/milestone
   */
  levelUp(): string {
    return generateSound({
      duration: 0.5,
      startFreq: 400,
      endFreq: 1200,
      waveform: 'square',
      envelope: { attack: 0.02, decay: 0.1, sustain: 0.6, release: 0.38 },
      volume: 0.55,
      vibrato: { rate: 10, depth: 60 },
    });
  },

  /**
   * Slow mode start - entering cautious state
   */
  slowModeStart(): string {
    return generateSound({
      duration: 0.25,
      startFreq: 600,
      endFreq: 300,
      waveform: 'sine',
      envelope: { attack: 0.03, decay: 0.08, sustain: 0.5, release: 0.14 },
      volume: 0.35,
      tremolo: { rate: 6, depth: 0.3 },
    });
  },

  /**
   * Slow mode tick - score accumulation pulse
   */
  slowModeTick(): string {
    return generateSound({
      duration: 0.06,
      startFreq: 800,
      endFreq: 600,
      waveform: 'sine',
      envelope: { attack: 0.005, decay: 0.02, sustain: 0.3, release: 0.035 },
      volume: 0.2,
    });
  },

  /**
   * Game start sound - match beginning fanfare
   */
  gameStart(): string {
    return generateSound({
      duration: 0.6,
      startFreq: 300,
      endFreq: 900,
      waveform: 'square',
      envelope: { attack: 0.03, decay: 0.15, sustain: 0.5, release: 0.42 },
      volume: 0.5,
      vibrato: { rate: 12, depth: 50 },
    });
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FEAST SOUNDS - Merged food celebration sounds
  // Each tier gets progressively more epic!
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Feast: Meal (+15) - Soft chime
   * Warm, gentle notification that food merged
   */
  feastMeal(): string {
    return generateSound({
      duration: 0.25,
      startFreq: 600,
      endFreq: 800,
      waveform: 'triangle',
      envelope: { attack: 0.01, decay: 0.08, sustain: 0.4, release: 0.16 },
      volume: 0.4,
    });
  },

  /**
   * Feast: Dinner (+30) - Ding!
   * Clear, pleasant bell-like sound
   */
  feastDinner(): string {
    return generateSound({
      duration: 0.3,
      startFreq: 700,
      endFreq: 1000,
      waveform: 'triangle',
      envelope: { attack: 0.008, decay: 0.1, sustain: 0.45, release: 0.19 },
      volume: 0.5,
      vibrato: { rate: 6, depth: 20 },
    });
  },

  /**
   * Feast: Feast (+50) - Fanfare
   * Exciting ascending celebration
   */
  feastFeast(): string {
    return generateSound({
      duration: 0.4,
      startFreq: 500,
      endFreq: 1200,
      waveform: 'square',
      envelope: { attack: 0.015, decay: 0.12, sustain: 0.5, release: 0.27 },
      volume: 0.55,
      vibrato: { rate: 10, depth: 40 },
    });
  },

  /**
   * Feast: Banquet (+100) - Pop + cheer
   * Champagne burst celebration sound
   */
  feastBanquet(): string {
    return generateSound({
      duration: 0.5,
      startFreq: 400,
      endFreq: 1400,
      waveform: 'square',
      envelope: { attack: 0.01, decay: 0.15, sustain: 0.55, release: 0.34 },
      volume: 0.65,
      vibrato: { rate: 14, depth: 60 },
    });
  },

  /**
   * Feast: Golden Buffet (+250) - Epic orchestra hit
   * Majestic golden announcement
   */
  feastGolden(): string {
    return generateSound({
      duration: 0.6,
      startFreq: 350,
      endFreq: 1600,
      waveform: 'square',
      envelope: { attack: 0.02, decay: 0.18, sustain: 0.6, release: 0.4 },
      volume: 0.75,
      vibrato: { rate: 16, depth: 80 },
    });
  },

  /**
   * Feast: Mythical Feast (+500) - Legendary fanfare + screen flash
   * The ultimate celebration - everyone hears this!
   */
  feastMythical(): string {
    return generateSound({
      duration: 0.8,
      startFreq: 300,
      endFreq: 2000,
      waveform: 'square',
      envelope: { attack: 0.025, decay: 0.2, sustain: 0.65, release: 0.55 },
      volume: 0.85,
      vibrato: { rate: 20, depth: 100 },
    });
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // LEVIATHAN SOUNDS - Boss-specific audio effects
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * LEVIATHAN Spawn - Deep, ominous rumble/roar
   * Announces the arrival of the ultimate predator
   */
  leviathanSpawn(): string {
    return generateSound({
      duration: 1.5,
      startFreq: 60,
      endFreq: 120,
      waveform: 'sawtooth',
      envelope: { attack: 0.3, decay: 0.4, sustain: 0.5, release: 0.8 },
      volume: 0.8,
      tremolo: { rate: 4, depth: 0.4 },
    });
  },

  /**
   * LEVIATHAN Death - Triumphant fanfare
   * Celebration when LEVIATHAN is slain
   */
  leviathanDeath(): string {
    return generateSound({
      duration: 1.2,
      startFreq: 400,
      endFreq: 1600,
      waveform: 'square',
      envelope: { attack: 0.05, decay: 0.3, sustain: 0.6, release: 0.85 },
      volume: 0.9,
      vibrato: { rate: 12, depth: 80 },
    });
  },

  /**
   * LEVIATHAN Nearby - Subtle tension/heartbeat
   * Plays when LEVIATHAN is within proximity
   */
  leviathanNearby(): string {
    return generateSound({
      duration: 0.8,
      startFreq: 50,
      endFreq: 70,
      waveform: 'sine',
      envelope: { attack: 0.1, decay: 0.2, sustain: 0.4, release: 0.5 },
      volume: 0.35,
      tremolo: { rate: 2, depth: 0.6 },
    });
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NEAR-MISS SOUNDS - Whoosh feedback for close calls
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Near miss whoosh - Quick swoosh sound for close encounters
   * A satisfying "that was close!" audio cue
   */
  nearMiss(): string {
    return generateSound({
      duration: 0.15,
      startFreq: 800,
      endFreq: 200,
      waveform: 'noise',
      envelope: { attack: 0.01, decay: 0.04, sustain: 0.3, release: 0.1 },
      volume: 0.35,
    });
  },

  /**
   * Near miss close whoosh - More intense for very close calls
   * Louder, longer whoosh with a lower pitch for dramatic effect
   */
  nearMissClose(): string {
    return generateSound({
      duration: 0.22,
      startFreq: 600,
      endFreq: 100,
      waveform: 'noise',
      envelope: { attack: 0.015, decay: 0.06, sustain: 0.4, release: 0.14 },
      volume: 0.5,
      tremolo: { rate: 20, depth: 0.2 },
    });
  },
};

/**
 * Generate all sound effects and return as a map of data URLs
 */
export function generateAllSounds(): Map<string, string> {
  const sounds = new Map<string, string>();

  // Gameplay sounds
  sounds.set('eat', SoundEffects.eat());
  sounds.set('eat_combo', SoundEffects.eatCombo());
  sounds.set('powerup', SoundEffects.powerup());
  sounds.set('boost_start', SoundEffects.boostStart());
  sounds.set('boost_loop', SoundEffects.boostLoop());
  sounds.set('boost_end', SoundEffects.boostEnd());
  sounds.set('death', SoundEffects.death());
  sounds.set('kill', SoundEffects.kill());
  sounds.set('combo', SoundEffects.combo());
  sounds.set('grow', SoundEffects.grow());
  sounds.set('spawn', SoundEffects.spawn());
  sounds.set('level_up', SoundEffects.levelUp());
  sounds.set('slow_mode_start', SoundEffects.slowModeStart());
  sounds.set('slow_mode_tick', SoundEffects.slowModeTick());

  // Feast sounds (merged food celebration)
  sounds.set('feast_meal', SoundEffects.feastMeal());
  sounds.set('feast_dinner', SoundEffects.feastDinner());
  sounds.set('feast_feast', SoundEffects.feastFeast());
  sounds.set('feast_banquet', SoundEffects.feastBanquet());
  sounds.set('feast_golden', SoundEffects.feastGolden());
  sounds.set('feast_mythical', SoundEffects.feastMythical());

  // UI sounds
  sounds.set('ui_click', SoundEffects.uiClick());
  sounds.set('ui_hover', SoundEffects.uiHover());
  sounds.set('ui_success', SoundEffects.uiSuccess());
  sounds.set('ui_error', SoundEffects.uiError());
  sounds.set('game_start', SoundEffects.gameStart());

  // LEVIATHAN sounds
  sounds.set('leviathan_spawn', SoundEffects.leviathanSpawn());
  sounds.set('leviathan_death', SoundEffects.leviathanDeath());
  sounds.set('leviathan_nearby', SoundEffects.leviathanNearby());

  // Near-miss sounds
  sounds.set('near_miss', SoundEffects.nearMiss());
  sounds.set('near_miss_close', SoundEffects.nearMissClose());

  return sounds;
}
